// Language context for multi-language support - v2
import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "fr" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations: Record<Language, Record<string, string>> = {
  fr: {
    // Navigation
    "nav.main": "Navigation principale",
    "nav.skills": "Compétences",
    "nav.experience": "Expérience",
    "nav.portfolio": "Portfolio",
    "nav.certifications": "Formation",
    "nav.contact": "Contact",
    
    // Hero
    "hero.role": "Chef de Projet Digital • Luxe & Cosmétiques • Data-driven",
    "hero.description": "Cheffe de projet digital spécialisée luxe et cosmétiques, orientée data-driven et omnicanal. 3 ans d'expérience chez Shiseido Group, Poiray et maisons de prestige. Expertise en pilotage e-commerce, CRM multi-marchés, automation marketing et stratégie digitale haut de gamme.",
    "hero.download": "Télécharger CV",
    "hero.contact": "Contact",
    "hero.available": "Disponible pour CDI",
    "hero.seeking": "En recherche active",
    
    // Companies
    "companies.title": "Entreprises avec lesquelles j'ai travaillé",
    
    // Fit Score
    "fitscore.title": "Score de Compatibilité",
    "fitscore.subtitle": "Fit Score",
    "fitscore.description": "Collez une offre d'emploi pour évaluer automatiquement la compatibilité avec mon profil",
    "fitscore.input.label": "Description du poste",
    "fitscore.input.placeholder": "Collez ici la description de l'offre d'emploi...",
    "fitscore.analyze": "Analyser la compatibilité",
    "fitscore.analyzing": "Analyse en cours...",
    "fitscore.excellent": "Excellente compatibilité !",
    "fitscore.good": "Bonne compatibilité",
    "fitscore.partial": "Compatibilité partielle",
    "fitscore.skills.matched": "Compétences correspondantes :",
    "fitscore.reset": "Nouvelle analyse",
    "fitscore.contact": "Me contacter",
    "fitscore.skill.project": "Gestion de projet",
    "fitscore.skill.luxury": "Secteur Luxe",
    
    // Skills
    "skills.title": "Compétences",
    "skills.subtitle": "Ce que je sais faire",
    "skills.cloud.title": "CRM & Emailing",
    "skills.cloud.description": "Gestion de campagnes emailing multi-marchés. Salesforce Marketing Cloud, automation, segmentation et suivi de base de données 240K contacts.",
    "skills.systems.title": "Analyse & Reporting",
    "skills.systems.description": "Création de tableaux de bord et rapports. Suivi des KPIs, analyse de performance, reporting hebdomadaire pour les équipes.",
    "skills.devops.title": "E-commerce",
    "skills.devops.description": "Gestion opérationnelle de sites e-commerce. Shopify et Magento, suivi des commandes, optimisation des fiches produit.",
    "skills.security.title": "Coordination d'équipes",
    "skills.security.description": "Travail avec équipes locales et agences externes. Organisation de réunions, suivi des livrables, interface prestataires.",
    "skills.finops.title": "Gestion de projet",
    "skills.finops.description": "Planification et suivi de projets digitaux. JIRA, Notion, respect des délais et coordination des intervenants.",
    "skills.migration.title": "Contenu & SEO",
    "skills.migration.description": "Rédaction de contenus web optimisés SEO. Fiches produit, articles, et respect des guidelines de marque.",
    
    // Experience
    "experience.title": "Expérience Professionnelle",
    "experience.subtitle": "Parcours",
    "experience.current": "Poste actuel",
    "experience.job1.title": "Webdesigner & Content Manager",
    "experience.job1.company": "Compagnie des Saveurs Exotiques",
    "experience.job1.period": "Janvier 2025 - Décembre 2025",
    "experience.job1.description": "Pilotage intégral de la refonte du site e-commerce sur Shopify, de la stratégie à la mise en ligne. Approche phygitale et clienteling digital.",
    "experience.job1.mission1": "Définition de la nouvelle arborescence UX, maquettes UI responsives, livraison en 8 semaines (-25% vs planning initial)",
    "experience.job1.mission2": "Création de contenus SEO optimisés : +40% de trafic organique, taux de rebond réduit de 15%",
    "experience.job1.mission3": "Construction d'une identité de marque digitale cohérente avec +20% d'engagement sur les réseaux sociaux",
    "experience.job1.mission4": "Mise en ligne avec score Lighthouse 95+, taux de conversion +12% vs ancien site",
    "experience.job2.title": "Chef de Projet CRM EMEA",
    "experience.job2.company": "Shiseido",
    "experience.job2.period": "Juillet 2024 - Décembre 2024",
    "experience.job2.description": "Élaboration et pilotage du calendrier CRM multimarques sur 6 pays européens via Salesforce Marketing Cloud.",
    "experience.job2.mission1": "Gestion de 180+ campagnes emailing, ROI moyen de 8:1 sur les campagnes promotionnelles",
    "experience.job2.mission2": "Coordination de 2 agences et 6 équipes locales, réduction de 20% des délais de production",
    "experience.job2.mission3": "Base de 240K contacts, taux d'ouverture 38% (+10 pts vs benchmark secteur), CTR 4.2%",
    "experience.job2.mission4": "Dashboards Tableau automatisés, reporting time réduit de 30%, NPS client +8 pts",
    "experience.job3.title": "Chef de Projet E-commerce",
    "experience.job3.company": "Poiray & Aurélie Bidermann",
    "experience.job3.period": "Septembre 2023 - Juin 2024",
    "experience.job3.description": "Gestion opérationnelle autonome des sites e-commerce des deux maisons de joaillerie de luxe. Expérience phygitale premium.",
    "experience.job3.mission1": "Traitement de 2700+ commandes (panier moyen 850€), taux de satisfaction client 96%",
    "experience.job3.mission2": "Reporting hebdomadaire avec analyse prédictive, anticipation des ruptures de stock de 15 jours",
    "experience.job3.mission3": "Optimisation Magento : +35% de ventes en période creuse, taux de conversion +18%",
    "experience.job3.mission4": "Stratégie retail media : ROAS 6:1 sur les campagnes Google Shopping",
    "experience.job4.title": "Chef de Projet E-commerce",
    "experience.job4.company": "Linea Chic",
    "experience.job4.period": "Décembre 2022 - Août 2023",
    "experience.job4.description": "Gestion et amélioration autonome des fiches produit sous Magento. Focus data-driven et SEO.",
    "experience.job4.mission1": "Rédaction SEO et optimisation : +15% de CTR, +25% de trafic organique sur le catalogue",
    "experience.job4.mission2": "Coordination shootings : réduction de 20% des coûts photo, time-to-market -30%",
    "experience.job4.mission3": "Service client premium : taux de résolution 95%, NPS +12 pts",
    "experience.job4.mission4": "Gestion catalogue 500+ références : taux de disponibilité 98%, rotation stock optimisée",
    
    // Certifications
    "certifications.title": "Formation & Certifications",
    "certifications.education": "Formation",
    "certifications.languages": "Langues",
    "certifications.inProgress": "En cours",
    "certifications.edu1.title": "NEOMA Business School",
    "certifications.edu1.subtitle": "MSc Luxury Marketing • Parcours International",
    "certifications.edu1.level": "Master 2 (2023)",
    "certifications.edu2.title": "Certifications Professionnelles (en cours)",
    "certifications.edu2.level": "Google Analytics • HubSpot Marketing • Agile/Scrum",
    "certifications.french": "Français",
    "certifications.french.level": "Natif",
    "certifications.english": "Anglais",
    "certifications.english.level": "C1 - Professionnel / Business",
    
    // Contact
    "contact.title": "Me Contacter",
    "contact.description": "Vous avez un projet ou une opportunité ? N'hésitez pas à me contacter.",
    "contact.name": "Nom complet",
    "contact.name.placeholder": "Votre nom",
    "contact.email": "Email",
    "contact.email.placeholder": "votre@email.com",
    "contact.subject": "Sujet",
    "contact.subject.placeholder": "Objet de votre message",
    "contact.message": "Message",
    "contact.message.placeholder": "Décrivez votre projet ou votre demande...",
    "contact.send": "Envoyer le message",
    "contact.sending": "Envoi en cours...",
    "contact.success.title": "Message envoyé avec succès !",
    "contact.success.description": "Je vous répondrai dans les plus brefs délais.",
    "contact.success.another": "Envoyer un autre message",
    "contact.error.name": "Le nom est requis",
    "contact.error.nameMax": "Le nom doit faire moins de 100 caractères",
    "contact.error.email": "Format d'email invalide",
    "contact.error.emailMax": "L'email doit faire moins de 255 caractères",
    "contact.error.subject": "Le sujet est requis",
    "contact.error.subjectMax": "Le sujet doit faire moins de 200 caractères",
    "contact.error.messageMin": "Le message doit faire au moins 10 caractères",
    "contact.error.messageMax": "Le message doit faire moins de 5000 caractères",
    
    // Portfolio
    "portfolio.title": "Projets réalisés",
    "portfolio.subtitle": "Mes réalisations",
    "portfolio.project1.title": "Refonte site e-commerce",
    "portfolio.project1.description": "Refonte complète du site Shopify : nouvelle arborescence, design responsive, coordination développeurs. Livré en 8 semaines.",
    "portfolio.project1.metric1.value": "+12%",
    "portfolio.project1.metric1.label": "conversion",
    "portfolio.project1.metric2.value": "+40%",
    "portfolio.project1.metric2.label": "trafic",
    "portfolio.project2.title": "Campagnes CRM chez Shiseido",
    "portfolio.project2.description": "Coordination de 180+ campagnes emailing sur 6 pays. Base de 240K contacts, pilotage de 2 agences.",
    "portfolio.project2.metric1.value": "ROI 8:1",
    "portfolio.project2.metric1.label": "campagnes",
    "portfolio.project2.metric2.value": "38%",
    "portfolio.project2.metric2.label": "taux d'ouverture",
    "portfolio.project3.title": "E-commerce joaillerie",
    "portfolio.project3.description": "Gestion quotidienne des sites Poiray et Aurélie Bidermann. 2700+ commandes traitées, panier moyen 850€.",
    "portfolio.project3.metric1.value": "+35%",
    "portfolio.project3.metric1.label": "ventes",
    "portfolio.project3.metric2.value": "96%",
    "portfolio.project3.metric2.label": "satisfaction",
    "portfolio.cta": "Intéressé(e) par mon profil ?",
    "portfolio.contact": "Planifier un échange",
    
    // Footer
    "footer.rights": "Tous droits réservés.",
  },
  en: {
    // Navigation
    "nav.main": "Main navigation",
    "nav.skills": "Skills",
    "nav.experience": "Experience",
    "nav.portfolio": "Portfolio",
    "nav.certifications": "Education",
    "nav.contact": "Contact",
    
    // Hero
    "hero.role": "Digital Project Manager • Luxury & Cosmetics • Data-driven",
    "hero.description": "Digital Project Manager specialized in luxury and cosmetics, with a data-driven and omnichannel approach. 3 years of experience at Shiseido Group, Poiray and prestigious brands. Expertise in e-commerce management, multi-market CRM, marketing automation and high-end digital strategy.",
    "hero.download": "Download CV",
    "hero.contact": "Contact",
    "hero.available": "Available for permanent position",
    "hero.seeking": "Actively looking",
    
    // Companies
    "companies.title": "Companies I've worked with",
    
    // Fit Score
    "fitscore.title": "Compatibility Score",
    "fitscore.subtitle": "Fit Score",
    "fitscore.description": "Paste a job description to automatically evaluate compatibility with my profile",
    "fitscore.input.label": "Job description",
    "fitscore.input.placeholder": "Paste the job description here...",
    "fitscore.analyze": "Analyze compatibility",
    "fitscore.analyzing": "Analyzing...",
    "fitscore.excellent": "Excellent match!",
    "fitscore.good": "Good match",
    "fitscore.partial": "Partial match",
    "fitscore.skills.matched": "Matching skills:",
    "fitscore.reset": "New analysis",
    "fitscore.contact": "Contact me",
    "fitscore.skill.project": "Project Management",
    "fitscore.skill.luxury": "Luxury Sector",
    
    // Skills
    "skills.title": "Skills",
    "skills.subtitle": "What I can do",
    "skills.cloud.title": "CRM & Emailing",
    "skills.cloud.description": "Multi-market email campaign management. Salesforce Marketing Cloud, automation, segmentation and 240K contact database management.",
    "skills.systems.title": "Analytics & Reporting",
    "skills.systems.description": "Dashboard creation and reporting. KPI tracking, performance analysis, weekly reports for teams.",
    "skills.devops.title": "E-commerce",
    "skills.devops.description": "Operational e-commerce site management. Shopify and Magento, order tracking, product page optimization.",
    "skills.security.title": "Team Coordination",
    "skills.security.description": "Working with local teams and external agencies. Meeting organization, deliverable tracking, vendor interface.",
    "skills.finops.title": "Project Management",
    "skills.finops.description": "Digital project planning and tracking. JIRA, Notion, deadline management and stakeholder coordination.",
    "skills.migration.title": "Content & SEO",
    "skills.migration.description": "SEO-optimized web content writing. Product pages, articles, and brand guideline compliance.",
    
    // Experience
    "experience.title": "Professional Experience",
    "experience.subtitle": "Career",
    "experience.current": "Current position",
    "experience.job1.title": "Webdesigner & Content Manager",
    "experience.job1.company": "Compagnie des Saveurs Exotiques",
    "experience.job1.period": "January 2025 - December 2025",
    "experience.job1.description": "Full management of e-commerce website redesign on Shopify, from strategy to launch. Phygital approach and digital clienteling.",
    "experience.job1.mission1": "New UX architecture definition, responsive UI mockups, delivery in 8 weeks (-25% vs initial planning)",
    "experience.job1.mission2": "SEO-optimized content creation: +40% organic traffic, bounce rate reduced by 15%",
    "experience.job1.mission3": "Building a coherent digital brand identity with +20% social media engagement",
    "experience.job1.mission4": "Launch with Lighthouse score 95+, conversion rate +12% vs old site",
    "experience.job2.title": "CRM Project Manager EMEA",
    "experience.job2.company": "Shiseido",
    "experience.job2.period": "July 2024 - December 2024",
    "experience.job2.description": "Development and management of multi-brand CRM calendar across 6 European countries via Salesforce Marketing Cloud.",
    "experience.job2.mission1": "Management of 180+ email campaigns, average ROI of 8:1 on promotional campaigns",
    "experience.job2.mission2": "Coordination of 2 agencies and 6 local teams, 20% reduction in production delays",
    "experience.job2.mission3": "240K contact database, 38% open rate (+10 pts vs industry benchmark), 4.2% CTR",
    "experience.job2.mission4": "Automated Tableau dashboards, reporting time reduced by 30%, customer NPS +8 pts",
    "experience.job3.title": "E-commerce Project Manager",
    "experience.job3.company": "Poiray & Aurélie Bidermann",
    "experience.job3.period": "September 2023 - June 2024",
    "experience.job3.description": "Autonomous operational management of e-commerce for two luxury fine jewelry houses. Premium phygital experience.",
    "experience.job3.mission1": "Processing of 2,700+ orders (average basket €850), 96% customer satisfaction rate",
    "experience.job3.mission2": "Weekly reporting with predictive analysis, 15-day stock-out anticipation",
    "experience.job3.mission3": "Magento optimization: +35% sales during slow periods, conversion rate +18%",
    "experience.job3.mission4": "Retail media strategy: ROAS 6:1 on Google Shopping campaigns",
    "experience.job4.title": "E-commerce Project Manager",
    "experience.job4.company": "Linea Chic",
    "experience.job4.period": "December 2022 - August 2023",
    "experience.job4.description": "Autonomous management and improvement of product pages on Magento. Data-driven and SEO focus.",
    "experience.job4.mission1": "SEO copywriting and optimization: +15% CTR, +25% organic traffic on catalog",
    "experience.job4.mission2": "Photoshoot coordination: 20% photo cost reduction, time-to-market -30%",
    "experience.job4.mission3": "Premium customer service: 95% resolution rate, NPS +12 pts",
    "experience.job4.mission4": "Catalog management 500+ references: 98% availability rate, optimized stock rotation",
    
    // Certifications
    "certifications.title": "Education & Certifications",
    "certifications.education": "Education",
    "certifications.languages": "Languages",
    "certifications.inProgress": "In progress",
    "certifications.edu1.title": "NEOMA Business School",
    "certifications.edu1.subtitle": "MSc Luxury Marketing • International Track",
    "certifications.edu1.level": "Master 2 (2023)",
    "certifications.edu2.title": "Professional Certifications (in progress)",
    "certifications.edu2.level": "Google Analytics • HubSpot Marketing • Agile/Scrum",
    "certifications.french": "French",
    "certifications.french.level": "Native",
    "certifications.english": "English",
    "certifications.english.level": "C1 - Professional / Business",
    
    // Contact
    "contact.title": "Contact Me",
    "contact.description": "Have a project or opportunity? Don't hesitate to get in touch.",
    "contact.name": "Full name",
    "contact.name.placeholder": "Your name",
    "contact.email": "Email",
    "contact.email.placeholder": "your@email.com",
    "contact.subject": "Subject",
    "contact.subject.placeholder": "Subject of your message",
    "contact.message": "Message",
    "contact.message.placeholder": "Describe your project or request...",
    "contact.send": "Send message",
    "contact.sending": "Sending...",
    "contact.success.title": "Message sent successfully!",
    "contact.success.description": "I will respond as soon as possible.",
    "contact.success.another": "Send another message",
    "contact.error.name": "Name is required",
    "contact.error.nameMax": "Name must be less than 100 characters",
    "contact.error.email": "Invalid email format",
    "contact.error.emailMax": "Email must be less than 255 characters",
    "contact.error.subject": "Subject is required",
    "contact.error.subjectMax": "Subject must be less than 200 characters",
    "contact.error.messageMin": "Message must be at least 10 characters",
    "contact.error.messageMax": "Message must be less than 5000 characters",
    
    // Portfolio
    "portfolio.title": "Projects delivered",
    "portfolio.subtitle": "My work",
    "portfolio.project1.title": "E-commerce website redesign",
    "portfolio.project1.description": "Complete Shopify redesign: new architecture, responsive design, developer coordination. Delivered in 8 weeks.",
    "portfolio.project1.metric1.value": "+12%",
    "portfolio.project1.metric1.label": "conversion",
    "portfolio.project1.metric2.value": "+40%",
    "portfolio.project1.metric2.label": "traffic",
    "portfolio.project2.title": "CRM campaigns at Shiseido",
    "portfolio.project2.description": "Coordination of 180+ email campaigns across 6 countries. 240K contact database, management of 2 agencies.",
    "portfolio.project2.metric1.value": "ROI 8:1",
    "portfolio.project2.metric1.label": "campaigns",
    "portfolio.project2.metric2.value": "38%",
    "portfolio.project2.metric2.label": "open rate",
    "portfolio.project3.title": "Jewelry e-commerce",
    "portfolio.project3.description": "Daily management of Poiray and Aurélie Bidermann websites. 2,700+ orders processed, €850 average basket.",
    "portfolio.project3.metric1.value": "+35%",
    "portfolio.project3.metric1.label": "sales",
    "portfolio.project3.metric2.value": "96%",
    "portfolio.project3.metric2.label": "satisfaction",
    "portfolio.cta": "Interested in my profile?",
    "portfolio.contact": "Schedule a call",
    
    // Footer
    "footer.rights": "All rights reserved.",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("language") as Language;
      if (saved) return saved;
      // Detect browser language
      const browserLang = navigator.language.split("-")[0];
      return browserLang === "fr" ? "fr" : "en";
    }
    return "fr";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
